'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';

export default function LoginPage() {
  const { sendMagicLink } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError('');

    try {
      await sendMagicLink(email.trim().toLowerCase());
      setSent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <Link href="/" className="flex items-center gap-2 mb-4">
            <AppLogo size={40} />
            <span className="font-extrabold text-xl text-foreground tracking-tight">DoggyYears</span>
          </Link>
          <p className="text-sm text-muted-foreground text-center">
            Celebrate every dog's story
          </p>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-card">
          {!sent ? (
            <>
              <div className="mb-6">
                <h1 className="text-xl font-700 text-foreground mb-1">Sign in to DoggyYears</h1>
                <p className="text-sm text-muted-foreground">
                  Enter your email and we'll send you a magic link to sign in instantly — no password needed.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-500 text-foreground mb-1.5">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoFocus
                      className="w-full pl-9 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-danger bg-danger/10 border border-danger/20 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-600 text-sm py-2.5 px-4 rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending magic link…
                    </>
                  ) : (
                    <>
                      Send Magic Link
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>

              <p className="mt-6 text-xs text-muted-foreground text-center">
                By signing in, you agree to our{' '}
                <Link href="/" className="text-primary hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link href="/" className="text-primary hover:underline">Privacy Policy</Link>.
              </p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
                  <CheckCircle size={28} className="text-success" />
                </div>
              </div>
              <h2 className="text-lg font-700 text-foreground mb-2">Check your inbox</h2>
              <p className="text-sm text-muted-foreground mb-1">
                We sent a magic link to
              </p>
              <p className="text-sm font-600 text-foreground mb-4">{email}</p>
              <p className="text-xs text-muted-foreground mb-6">
                Click the link in the email to sign in. The link expires in 1 hour.
                If you don't see it, check your spam folder.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(''); }}
                className="text-sm text-primary hover:underline font-500"
              >
                Use a different email
              </button>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link href="/" className="hover:text-foreground transition-colors">
            ← Back to DoggyYears
          </Link>
        </p>
      </div>
    </div>
  );
}
